import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut } from "lucide-react";
import { imgUrl } from "@/lib/content";

const MAX = 4;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

const usePinch = (onSwipe) => {
  const [view, setView] = useState({ s: 1, x: 0, y: 0 });
  const pts = useRef(new Map());
  const g = useRef({});
  const lastTap = useRef(0);
  const box = useRef(null);
  const bound = (v) => {
    const r = box.current?.getBoundingClientRect();
    if (!r || v.s <= 1) return { s: Math.max(1, v.s), x: 0, y: 0 };
    const mx = ((v.s - 1) * r.width) / 2, my = ((v.s - 1) * r.height) / 2;
    return { s: v.s, x: clamp(v.x, -mx, mx), y: clamp(v.y, -my, my) };
  };
  const set = (v) => setView(bound(v));
  const down = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    pts.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const [a, b] = [...pts.current.values()];
    g.current = b ? { d: dist(a, b), s: view.s } : { sx: e.clientX, sy: e.clientY, vx: view.x, vy: view.y, moved: false };
  };
  const move = (e) => {
    if (!pts.current.has(e.pointerId)) return;
    pts.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const [a, b] = [...pts.current.values()];
    if (b && g.current.d) { set({ ...view, s: clamp((g.current.s * dist(a, b)) / g.current.d, 1, MAX) }); return; }
    const dx = e.clientX - g.current.sx, dy = e.clientY - g.current.sy;
    if (Math.abs(dx) + Math.abs(dy) > 6) g.current.moved = true;
    if (view.s > 1) set({ ...view, x: g.current.vx + dx, y: g.current.vy + dy });
  };
  const up = (e) => {
    pts.current.delete(e.pointerId);
    const { sx, moved } = g.current;
    if (pts.current.size === 0 && view.s === 1 && moved && sx !== undefined && Math.abs(e.clientX - sx) > 60) onSwipe(e.clientX < sx ? 1 : -1);
    if (pts.current.size === 0 && !moved && sx !== undefined) {
      const now = Date.now();
      if (now - lastTap.current < 320) { set(view.s > 1 ? { s: 1, x: 0, y: 0 } : { s: 2.5, x: 0, y: 0 }); lastTap.current = 0; }
      else lastTap.current = now;
    }
    if (pts.current.size === 1) { const [a] = pts.current.values(); g.current = { sx: a.x, sy: a.y, vx: view.x, vy: view.y, moved: true }; }
  };
  const wheel = (e) => set({ ...view, s: clamp(view.s * (e.deltaY < 0 ? 1.15 : 0.87), 1, MAX) });
  return { view, set, box, handlers: { onPointerDown: down, onPointerMove: move, onPointerUp: up, onPointerCancel: up, onWheel: wheel } };
};

export const PhotoZoom = ({ images, start = 0, alt, onClose }) => {
  const [idx, setIdx] = useState(start < images.length ? start : 0);
  const go = (d) => { setIdx((i) => (i + d + images.length) % images.length); pinch.set({ s: 1, x: 0, y: 0 }); };
  const pinch = usePinch((d) => images.length > 1 && go(d));
  const { view, set, box, handlers } = pinch;
  useEffect(() => {
    const key = (e) => { if (e.key === "ArrowRight" && images.length > 1) go(1); if (e.key === "ArrowLeft" && images.length > 1) go(-1); };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }); // eslint-disable-line react-hooks/exhaustive-deps
  const zoom = (f) => set({ ...view, s: clamp(view.s * f, 1, MAX) });
  return createPortal(
    <div className="pz" role="dialog" aria-modal="true" aria-label={`${alt} photo`} data-testid="photo-zoom">
      <div className="pz-stage" ref={box} {...handlers} data-testid="photo-zoom-stage">
        <img src={imgUrl(images[idx], 1600)} alt={alt} draggable={false} className="pz-img" data-testid="photo-zoom-image"
          style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.s})` }} />
      </div>
      <button type="button" className="pz-btn pz-close" onClick={onClose} aria-label="Close" data-testid="photo-zoom-close"><X size={20} /></button>
      <div className="pz-tools">
        <button type="button" className="pz-btn" onClick={() => zoom(0.7)} disabled={view.s <= 1} aria-label="Zoom out" data-testid="photo-zoom-out"><ZoomOut size={18} /></button>
        <span className="pz-hint" data-testid="photo-zoom-counter">{images.length > 1 ? `${idx + 1} / ${images.length}` : "Pinch or double-tap to zoom"}</span>
        <button type="button" className="pz-btn" onClick={() => zoom(1.5)} disabled={view.s >= MAX} aria-label="Zoom in" data-testid="photo-zoom-in"><ZoomIn size={18} /></button>
      </div>
      {images.length > 1 && <>
        <button type="button" className="pz-btn pz-nav pz-prev" onClick={() => go(-1)} aria-label="Previous photo" data-testid="photo-zoom-prev"><ChevronLeft size={22} /></button>
        <button type="button" className="pz-btn pz-nav pz-next" onClick={() => go(1)} aria-label="Next photo" data-testid="photo-zoom-next"><ChevronRight size={22} /></button>
      </>}
    </div>,
    document.body,
  );
};
